import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import NumberInput from '../../Components/NumberInput';
import { SaveRoomFloorAreaInput } from '../../api/roomFloorAreaApi';
import {
    FloorAreaVerificationState,
    MAX_FLOOR_AREA,
    roundArea,
    splitAreaProportionally,
    validateFloorAreaSplit,
} from '../../Helpers/floorAreaVerification';

/** One floor component of the room as the dialog lists it. */
export interface FloorAreaRow {
    componentKey: string;
    name: string;
    /** Sum of the floor's shapes today, null when it has none. */
    currentArea: number | null;
}

interface RoomFloorAreaModalProps {
    show: boolean;
    onHide: () => void;
    floors: FloorAreaRow[];
    /** Today's golvyta (sum of the floors), 0 when unknown. */
    currentTotal: number;
    verificationState: FloorAreaVerificationState;
    onSave: (input: SaveRoomFloorAreaInput) => Promise<void>;
}

const areaOrNull = (value: number): number | null => (value > 0 ? roundArea(value) : null);

/**
 * Golvyta pencil on the room page (web#1383). The total is typed in; with
 * several floors it is spread over them (proportionally to their current
 * areas, editable per floor) and the rows must add up to it. Ticking
 * "Markera som uppmätt på ritning" records the verification on the room;
 * saving unticked removes one.
 */
const RoomFloorAreaModal: React.FC<RoomFloorAreaModalProps> = ({
    show,
    onHide,
    floors,
    currentTotal,
    verificationState,
    onSave,
}) => {
    const currentAreas = useMemo(() => floors.map(floor => floor.currentArea ?? 0), [floors]);
    const multiple = floors.length > 1;

    const [total, setTotal] = useState<number | null>(null);
    const [rows, setRows] = useState<number[]>([]);
    const [verify, setVerify] = useState(false);
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Initialise from the room when opened. Only on open: the building is
    // refetched inside onSave, and the form must not reset under a save.
    useEffect(() => {
        if (!show) return;
        setTotal(areaOrNull(currentTotal));
        setRows(currentAreas.map(roundArea));
        const verified = verificationState.kind === 'verified';
        setVerify(verified);
        setComment(verified ? verificationState.verification.comment ?? '' : '');
        setSaving(false);
        setSaveError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const effectiveRows = multiple ? rows : [total ?? 0];
    const validationError = validateFloorAreaSplit(total ?? 0, effectiveRows);
    const rowsSum = rows.reduce((sum, row) => sum + row, 0);

    const spread = (nextTotal: number | null) => {
        if (!multiple) return;
        setRows(nextTotal && nextTotal > 0 ? splitAreaProportionally(nextTotal, currentAreas) : currentAreas.map(roundArea));
    };

    const handleTotalChange = (value: number) => {
        const next = value > 0 ? value : null;
        setTotal(next);
        setSaveError(null);
        spread(next);
    };

    const handleRowChange = (index: number, value: number) => {
        setRows(prev => prev.map((row, i) => (i === index ? value : row)));
        setSaveError(null);
    };

    const handleClose = () => {
        if (!saving) onHide();
    };

    const handleSave = async () => {
        if (validationError || total === null) return;
        setSaving(true);
        setSaveError(null);
        try {
            await onSave({
                total,
                floors: floors.map((floor, i) => ({
                    componentKey: floor.componentKey,
                    currentArea: floor.currentArea,
                    newArea: effectiveRows[i],
                })),
                verify,
                comment: verify ? comment : undefined,
            });
            onHide();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Kunde inte spara golvytan. Försök igen.');
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Ändra golvyta</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={e => { e.preventDefault(); handleSave(); }}>
                    <Form.Group className="mb-3" controlId="roomFloorAreaTotal">
                        <Form.Label>Golvyta (kvm)</Form.Label>
                        <NumberInput
                            className="form-control"
                            value={total}
                            emptyValue={0}
                            onChange={handleTotalChange}
                            min={0}
                            max={MAX_FLOOR_AREA}
                            step={0.1}
                            placeholder="Ange golvyta i kvm"
                            disabled={saving}
                            autoFocus
                        />
                        <Form.Text className="text-muted">
                            {multiple
                                ? 'Rummets golvyta är summan av golvens ytor. Ange den nya totalen och fördela den på golven nedan.'
                                : 'Golvets form ersätts med den angivna ytan.'}
                        </Form.Text>
                    </Form.Group>

                    {multiple && (
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-semibold">Per golv</span>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    onClick={() => spread(total)}
                                    disabled={saving || !total}
                                >
                                    Fördela proportionellt
                                </Button>
                            </div>
                            {floors.map((floor, index) => (
                                <Form.Group
                                    key={floor.componentKey}
                                    className="d-flex align-items-center gap-2 mb-2"
                                    controlId={`roomFloorAreaRow-${floor.componentKey}`}
                                >
                                    <Form.Label className="mb-0 flex-grow-1" style={{ minWidth: 0 }}>
                                        <span className="d-block text-truncate">{floor.name}</span>
                                        <small className="text-muted">
                                            {floor.currentArea !== null ? `Idag ${floor.currentArea.toFixed(1)} kvm` : 'Ingen yta idag'}
                                        </small>
                                    </Form.Label>
                                    <NumberInput
                                        className="form-control"
                                        style={{ width: '120px' }}
                                        value={rows[index] ?? 0}
                                        emptyValue={0}
                                        onChange={value => handleRowChange(index, value)}
                                        min={0}
                                        max={MAX_FLOOR_AREA}
                                        step={0.1}
                                        disabled={saving}
                                    />
                                </Form.Group>
                            ))}
                            <small className={rowsSum > 0 && validationError?.includes('tillsammans') ? 'text-danger' : 'text-muted'}>
                                Summa {rowsSum.toFixed(1)} kvm{total ? ` av ${total.toFixed(1)} kvm` : ''}
                            </small>
                        </div>
                    )}

                    <Form.Group className="mb-3" controlId="roomFloorAreaVerify">
                        <Form.Check
                            type="checkbox"
                            label="Markera som uppmätt på ritning"
                            checked={verify}
                            onChange={e => setVerify(e.target.checked)}
                            disabled={saving}
                        />
                        <Form.Text className="text-muted">
                            Ersätter inventeringens uppskattning. Ändras ett golv i appen efteråt gäller markeringen inte längre.
                        </Form.Text>
                    </Form.Group>

                    {verify && (
                        <Form.Group className="mb-3" controlId="roomFloorAreaComment">
                            <Form.Label>Kommentar</Form.Label>
                            <Form.Control
                                type="text"
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                                placeholder="T.ex. vilken ritning ytan är mätt på"
                                disabled={saving}
                            />
                        </Form.Group>
                    )}

                    {validationError && total !== null && (
                        <div className="text-danger small">{validationError}</div>
                    )}
                    {saveError && <Alert variant="danger" className="mt-2 mb-0">{saveError}</Alert>}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={saving}>Avbryt</Button>
                <Button variant="primary" onClick={handleSave} disabled={saving || !!validationError}>
                    {saving ? 'Sparar...' : 'Spara'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RoomFloorAreaModal;
